import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Search, User, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUSES = ["CHECKED IN", "BOARDED", "AT DESTINATION", "DEPARTED", "RETURNED", "RELEASED"];

export default function AdminCheckIn() {
  const [experiences, setExperiences] = useState([]);
  const [expId, setExpId] = useState("");
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [releaseTo, setReleaseTo] = useState("");

  useEffect(() => {
    api.get("/experiences?include_hidden=true").then((r) => setExperiences(r.data)).catch(() => {});
  }, []);

  const lookup = async () => {
    if (!expId || !q) { toast.error("Choose an experience and enter a student ID or name."); return; }
    try {
      const { data } = await api.get(`/admin/checkin/lookup`, { params: { exp_id: expId, q } });
      setResult(data);
    } catch (e) { setResult(null); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doStatus = async (status) => {
    if (status === "RELEASED" && !releaseTo) { toast.error("Select the authorized adult for release."); return; }
    try {
      await api.post("/admin/checkin", {
        experience_id: expId, student_id: result.student_id, registration_id: result.registration_id,
        status, released_to: status === "RELEASED" ? releaseTo : null,
      });
      toast.success(`Marked ${status}.`);
      setResult({ ...result, checkin_status: status });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell admin>
      <h1 className="font-display text-3xl font-bold text-navy">Check-In / Check-Out</h1>
      <p className="text-slateBrand">Look up a student by QR code (Student ID) or name.</p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Select value={expId} onValueChange={setExpId}>
          <SelectTrigger data-testid="checkin-exp-select" className="h-12 sm:w-72"><SelectValue placeholder="Select experience" /></SelectTrigger>
          <SelectContent>{experiences.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="checkin-search" className="h-12 pl-10" placeholder="HEA-XXXXXX or student name" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} />
        </div>
        <Button data-testid="checkin-lookup-btn" onClick={lookup} className="h-12 bg-brandblue font-display font-semibold hover:bg-blue-700">Look Up</Button>
      </div>

      {result && (
        <div className="mt-8 max-w-lg rounded-2xl border border-slate-200 bg-white p-6" data-testid="checkin-result-card">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brandblue/10"><User className="h-8 w-8 text-brandblue" /></div>
            <div>
              <div className="font-display text-xl font-bold text-navy">{result.name}</div>
              <div className="text-sm text-slateBrand">Current status: <span className="font-semibold text-navy">{result.checkin_status || "Registered"}</span></div>
            </div>
          </div>

          {result.allergies && <div className="mt-4 flex items-center gap-2 rounded-lg bg-alertBrand/5 px-3 py-2 text-sm font-semibold text-alertBrand"><AlertTriangle className="h-4 w-4" />Allergy: {result.allergies}</div>}
          {result.accommodations && <div className="mt-2 flex items-center gap-2 rounded-lg bg-gold/10 px-3 py-2 text-sm text-[#8A5F00]"><AlertTriangle className="h-4 w-4" />{result.accommodations}</div>}

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">{result.payment_status === "paid" ? <CheckCircle2 className="h-4 w-4 text-successBrand" /> : <XCircle className="h-4 w-4 text-alertBrand" />}Payment {result.payment_status}</div>
            <div className="flex items-center gap-2">{result.forms_complete ? <CheckCircle2 className="h-4 w-4 text-successBrand" /> : <XCircle className="h-4 w-4 text-alertBrand" />}Forms {result.forms_complete ? "complete" : "missing"}</div>
            <div className="col-span-2 text-slateBrand">Transport: <span className="font-semibold capitalize text-navy">{result.transport_option}</span></div>
          </div>

          <div className="mt-5 border-t border-slate-100 pt-5">
            <Label className="font-display font-semibold text-navy">Authorized pickup (for release)</Label>
            <Select value={releaseTo} onValueChange={setReleaseTo}>
              <SelectTrigger data-testid="checkin-release-select" className="mt-1.5 h-11"><SelectValue placeholder="Select authorized adult" /></SelectTrigger>
              <SelectContent>{(result.authorized_pickups || []).map((p, i) => <SelectItem key={i} value={p.name}>{p.name} ({p.relationship})</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {STATUSES.map((s) => (
              <Button key={s} data-testid={`checkin-status-${s.toLowerCase().replace(/ /g, "-")}`} variant={result.checkin_status === s ? "default" : "outline"}
                onClick={() => doStatus(s)} className={`text-xs ${result.checkin_status === s ? "bg-brandblue" : ""}`}>{s}</Button>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
